import React, { Dispatch, SetStateAction, useRef, useState } from "react";
import { PrimeReactDropDown, ScheduleDepartmentDropDown, ScheduleRoleDropDown, ShiftDropDown, ToastMessageProps } from "../../constants/staticTypes";
import { Sidebar } from "primereact/sidebar";
import { JTranslation } from "../../helpers/jTranslate";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FieldArray, Form, FormikProvider, useFormik } from "formik";
import { ADD_NEW_SHIFT, BD, CANCEL, CLOSE, SAVE } from "../../constants/strings";
import { updateMultipleAssignShiftMultipleUsers } from "../../helpers/workTimeScheduleHelper";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import MlxTimePicker from "../mlx_time_picker/MlxTimePicker";
import dayjs from "dayjs";
import { InputSwitch, InputSwitchChangeEvent } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import { getTimezoneInfo, toPascalCase } from "../../helpers/utils";
import { capitalize, cloneDeep } from "lodash";
import * as Yup from 'yup';
import { parseTimeForTimePicker } from '../../helpers/newsAndEventsHelper';
import useMutationHook from "../../hooks/useMutationHook";
import { useQueryClient } from "react-query";
import { STAFFS_WITH_SCHEDULES_AND_SHIFT } from "../../constants/queryKeys";
import { Button } from 'antd';
import { DATE_FORMAT } from "../../constants/constants";

type Props = {
    visible: boolean;
    setVisible: React.Dispatch<React.SetStateAction<any>>;
    setToastMessage: Dispatch<SetStateAction<ToastMessageProps>>;
    selectedWeek: dayjs.Dayjs;
    departments: ScheduleDepartmentDropDown[];
    roles: ScheduleRoleDropDown[];
    shifts: ShiftDropDown[];
    staffs: PrimeReactDropDown[];
    scheduleId: string;
    staffShits: any[];
    callBackFn?: () => void
};

type FormData = {
    date: string;
    departmentId: string;
    departmentName: string;
    endTime: string;
    isBd: boolean;
    isClose: boolean;
    role: string;
    roleId: string;
    shiftName: string;
    startTime: string;
    tenantStaffIds: string[];
    roleOptions?: ScheduleRoleDropDown[];
    staffs?: PrimeReactDropDown[];
};

function AddMultipleShiftToMultipleUsersModal(
    {
        visible,
        setVisible,
        setToastMessage,
        selectedWeek,
        departments,
        roles,
        shifts,
        staffs,
        scheduleId,
        staffShits,
        callBackFn
    }: Readonly<Props>
) {
    const queryClient = useQueryClient();
    const addMultipleShiftsMutation = useMutationHook(queryClient, false);
    const addMultipleShiftRef = useRef(null);
    const { defaultDateFormat } = getTimezoneInfo();
    const weekdays = Array.from({ length: 7 }, (_, index) => ({
        day: selectedWeek.startOf('week').add(index, 'day').format('dddd').toLowerCase(),
        date: selectedWeek.startOf('week').add(index, 'day').format('YYYY-MM-DD'),
        parsedDate: selectedWeek.startOf('week').add(index, 'day').format(defaultDateFormat ?? DATE_FORMAT),
    }));
    const departmentList = [...departments].sort((a, b) => a.depName.localeCompare(b.depName));
    const roleList = [...roles].sort((a, b) => a.name.localeCompare(b.name));
    const staffList = [...staffs];
    const shiftList = [...shifts].map(shift => {
        if (shift?.name === 'Custom Shift') return shift;

        const newCodeName = shift?.code?.replace(/\s/g, '-');
        return { ...shift, code: `${newCodeName}-${shift.startTime}-${shift.endTime}` }
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const closeModal = () => {
        if (!visible) return;
        setVisible(undefined);
    };

    const onceApplied = (async (message: string, variant: string) => {
        setToastMessage({ message, variant, show: true });
        await queryClient.refetchQueries([STAFFS_WITH_SCHEDULES_AND_SHIFT]);
        setIsLoading(false);
        closeModal();

        if(callBackFn) callBackFn();
    });

    const validationSchema = Yup.object({
        shifts: Yup.array().of(
            Yup.object({
                date: Yup.string().required("Date is required"),
                departmentId: Yup.string().required("Department ID is required"),
                departmentName: Yup.string().required("Department Name is required"),
                role: Yup.string().required("Role is required"),
                roleId: Yup.string().required("Role ID is required"),
                shiftName: Yup.string().required("Shift name is required"),
                startTime: Yup.string().required("Start time is required"),
                endTime: Yup.string()
                    .required("End time is required")
                    .test('time-range', 'End time must be at least 1 hour later than Start time', function (_endTime, { parent }) {
                        const updatedStartTime = parseTimeForTimePicker({ formattedTime: parent.startTime })
                        const updatedEndTime = parseTimeForTimePicker({ formattedTime: parent.endTime })
                        const startDateTime = dayjs(updatedStartTime)
                        const endDateTime = dayjs(updatedEndTime)
                        return endDateTime.isAfter(startDateTime.add(59, 'minute'))
                    }),
                tenantStaffIds: Yup.array()
                    .min(1, "At least one staff member must be selected")
                    .of(Yup.string().required("Staff ID is required")),
            })
        ),
    });

    const multipleAssignShiftsForm = useFormik({
        initialValues: { shifts: [] as FormData[] },
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const { shifts: formData } = values;

            if (formData.length === 0) return;

            const params = formData.map((shift) => {
                const startTime = parseTimeForTimePicker({ formattedTime: shift.startTime });
                const endTime = parseTimeForTimePicker({ formattedTime: shift.endTime });
                const shiftName = shiftList.find(currentShift => currentShift?.code === shift?.shiftName)?.name;
                return {
                    date: shift.date,
                    departmentId: shift.departmentId,
                    departmentName: shift.departmentName,
                    isBd: shift.isBd,
                    isClose: shift.isClose,
                    role: shift.role,
                    roleId: shift.roleId,
                    shiftName: shiftName,
                    startTime: startTime?.format('hh:mm A'),
                    endTime: endTime?.format('hh:mm A'),
                    tenantStaffIds: shift.tenantStaffIds
                }
            });

            setIsLoading(true);
            updateMultipleAssignShiftMultipleUsers(
                addMultipleShiftsMutation,
                scheduleId,
                params,
                onceApplied,
                onceApplied
            );
        }
    });

    // check if staff has the same selected shift for the current date
    const getIfStaffHasShift = (option: any, data: any) => {
        const existingStaffShift = staffShits.find(staff => staff.id === option.code)?.shifts;
        if (existingStaffShift) {
            const currentShift = existingStaffShift[data?.date];
            const currentStart = parseTimeForTimePicker({ formattedTime: data?.startTime }) as dayjs.Dayjs;
            const currentEnd = parseTimeForTimePicker({ formattedTime: data?.endTime }) as dayjs.Dayjs;

            // check if staff has the same shift time for the current date
            const checkIfShiftExists = currentShift?.find((shift: any) => {                
                const otherStart = dayjs(shift.assignedShifts.startTime, "hh:mm A");        
                const otherEnd = dayjs(shift.assignedShifts.endTime, "hh:mm A");
                return (
                    (currentStart?.isSame(otherStart) || currentStart?.isBetween(otherStart, otherEnd)) ||
                    (currentEnd?.isSame(otherEnd) || currentEnd?.isBetween(otherStart, otherEnd)) ||
                    (otherStart?.isBetween(currentStart, currentEnd))
                );
            })

            // check if form data's staff has the same shift time for the current date
            const checkAlreadySelectedTime = multipleAssignShiftsForm.values.shifts.filter(shift => {
                if(shift?.date !== data?.date) return false;

                const otherStart = dayjs(shift.startTime, "HH:mm:ss");        
                const otherEnd = dayjs(shift.endTime, "HH:mm:ss");
                return (
                    ((currentStart?.isSame(otherStart) || currentStart?.isBetween(otherStart, otherEnd)) ||
                    (currentEnd?.isSame(otherEnd) || currentEnd?.isBetween(otherStart, otherEnd)) ||
                    (otherStart?.isBetween(currentStart, currentEnd))) && 
                    shift.tenantStaffIds.includes(option?.code)
                );
            })

            if (checkIfShiftExists || checkAlreadySelectedTime?.length) {
                return true
            }
        }
    }

    return (
        <Sidebar
            ref={addMultipleShiftRef}
            visible={visible}
            position="bottom"
            onHide={() => setVisible(false)}
            header={<h5 className="py-1"><JTranslation typeCase="pascal" text={"Assign Multiple Shifts"} /></h5>}
            style={{ height: 'calc(100% - 45%)' }}
            closeOnEscape={false}
            pt={{
                content: { style: { padding: '0rem' } },
                closeIcon: { style: { width: '1.5rem', height: '1.5rem' } }
            }}
        >
            <FormikProvider value={multipleAssignShiftsForm}>
                <Form className="card h-100">
                    <FieldArray name="shifts">
                        {({ push, remove, form }) => {
                            const errors = form?.errors?.shifts as any[]
                            const touched = form?.touched?.shifts as any[]
                            return (
                                <>
                                    <DataTable
                                        value={form.values.shifts}
                                        showGridlines
                                        scrollable
                                        scrollHeight='100%'
                                        className="work-time-schedule-table d-flex h-100 flex-column"
                                        pt={{
                                            loadingOverlay: { style: { zIndex: 3 } },
                                            wrapper: { style: { minHeight: 'calc(100% - 62px)' } }
                                        }}
                                        emptyMessage={
                                            <div className='d-flex justify-content-center align-items-center'>
                                                <JTranslation typeCase="capitalize" text={'No shifts added yet'} />
                                            </div>
                                        }
                                    >
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Date'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <Dropdown
                                                        value={data?.date}
                                                        onChange={(e: DropdownChangeEvent) => {
                                                            form.setFieldValue(`shifts.${rowIndex}.date`, e?.value ?? null);

                                                            // clear selected staffs
                                                            form.setFieldValue(`shifts.${rowIndex}.tenantStaffIds`, []);
                                                        }}
                                                        options={weekdays}
                                                        disabled={!weekdays.length}
                                                        optionLabel="parsedDate"
                                                        optionValue="date"
                                                        placeholder={"Select Date"}
                                                        className={`w-100 ${touched?.[rowIndex]?.date && errors?.[rowIndex]?.date && 'border-danger'}`}
                                                        pt={{
                                                            root: { style: { borderRadius: '3px' } },
                                                            input: { style: { padding: '0.375rem 0.75rem' } },
                                                            panel: {
                                                                style: {
                                                                    "--custom-dropdown-z-index": (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex
                                                                        ? (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                                        : 1056
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '8rem', maxWidth: '8rem' }}
                                            headerStyle={{ height: '50px' }}
                                            pt={{
                                                headerContent: { style: { justifyContent: 'start' } }
                                            }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Area of Operation'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <Dropdown
                                                        className={`w-100 ${touched?.[rowIndex]?.departmentId && errors?.[rowIndex]?.departmentId && 'border-danger'}`}
                                                        value={data?.departmentId}
                                                        onChange={(e: DropdownChangeEvent) => {
                                                            const { value } = e;
                                                            form.setFieldValue(`shifts.${rowIndex}.departmentId`, value ?? null);

                                                            // update dept name
                                                            const departmentName = departmentList.find(dept => dept.departmentId === value)?.depName;
                                                            form.setFieldValue(`shifts.${rowIndex}.departmentName`, departmentName ?? null);

                                                            // Update role options dynamically based on selected department
                                                            const filteredRoles = roleList.filter((role) => role.roleDepartmentId === value);
                                                            form.setFieldValue(`shifts.${rowIndex}.roleOptions`, filteredRoles ?? []);
                                                        }}
                                                        options={departmentList}
                                                        disabled={!departmentList.length}
                                                        optionLabel="depName"
                                                        optionValue="departmentId"
                                                        placeholder={"Select Area"}
                                                        filter
                                                        pt={{
                                                            root: { style: { borderRadius: '3px' } },
                                                            input: { style: { padding: '0.375rem 0.75rem' } },
                                                            panel: {
                                                                style: {
                                                                    "--custom-dropdown-z-index": (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex
                                                                        ? (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                                        : 1056
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '10rem', maxWidth: '10rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Role'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <Dropdown
                                                        className={`w-100 ${touched?.[rowIndex]?.roleId && errors?.[rowIndex]?.roleId && 'border-danger'}`}
                                                        value={data?.roleId}
                                                        onChange={(e: DropdownChangeEvent) => {
                                                            const { value } = e;
                                                            form.setFieldValue(`shifts.${rowIndex}.roleId`, value ?? null);

                                                            // update dept name
                                                            const roleName = roleList.find(role => role?.code === value)?.name;
                                                            form.setFieldValue(`shifts.${rowIndex}.role`, roleName ?? null);
                                                        }}
                                                        options={data?.roleOptions ?? []}
                                                        disabled={!data?.roleOptions?.length}
                                                        optionLabel="name"
                                                        optionValue="code"
                                                        placeholder={"Select Role"}
                                                        filter
                                                        pt={{
                                                            root: { style: { borderRadius: '3px' } },
                                                            input: { style: { padding: '0.375rem 0.75rem' } },
                                                            panel: {
                                                                style: {
                                                                    "--custom-dropdown-z-index": (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex
                                                                        ? (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                                        : 1056
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '8rem', maxWidth: '8rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Shift'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <Dropdown
                                                        className={`w-100 ${touched?.[rowIndex]?.shiftName && errors?.[rowIndex]?.shiftName && 'border-danger'}`}
                                                        value={data?.shiftName}
                                                        onChange={(e: DropdownChangeEvent) => {
                                                            const { value } = e;
                                                            form.setFieldValue(`shifts.${rowIndex}.shiftName`, value ?? null);

                                                            // update start and end time
                                                            if (value !== 'Custom Shift') {
                                                                const shift = shiftList.find(shift => shift.code === value);
                                                                form.setFieldValue(`shifts.${rowIndex}.startTime`, shift?.startTime ?? null);
                                                                form.setFieldValue(`shifts.${rowIndex}.endTime`, shift?.endTime ?? null);
                                                            } else {
                                                                form.setFieldValue(`shifts.${rowIndex}.startTime`, null);
                                                                form.setFieldValue(`shifts.${rowIndex}.endTime`, null);
                                                            }

                                                            // clear selected staffs
                                                            form.setFieldValue(`shifts.${rowIndex}.tenantStaffIds`, []);
                                                        }}
                                                        options={shiftList ?? []}
                                                        disabled={!shiftList?.length}
                                                        optionLabel="name"
                                                        optionValue="code"
                                                        itemTemplate={(option) => <>{capitalize(option?.name)}</>}
                                                        valueTemplate={(option) => {
                                                            return option ? <>{capitalize(option?.name)}</> : <>&nbsp;</>;
                                                        }}
                                                        placeholder={"Select Shift"}
                                                        filter
                                                        pt={{
                                                            root: { style: { borderRadius: '3px' } },
                                                            input: { style: { padding: '0.375rem 0.75rem' } },
                                                            panel: {
                                                                style: {
                                                                    "--custom-dropdown-z-index": (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex
                                                                        ? (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                                        : 1056
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '8rem', maxWidth: '8rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Start Time'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <MlxTimePicker
                                                        value={data?.startTime ? dayjs(data?.startTime, 'HH:mm:ss') : null}
                                                        onChange={(time) => {
                                                            form.setFieldValue(`shifts.${rowIndex}.startTime`, time?.format('HH:mm:ss') ?? null);

                                                            // clear selected staffs
                                                            form.setFieldValue(`shifts.${rowIndex}.tenantStaffIds`, []);
                                                        }}
                                                        className={`from-control ${data?.shiftName === 'Custom Shift' && errors?.[rowIndex]?.startTime && 'border-danger'}`}
                                                        popupStyle={{
                                                            zIndex: (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                        }}
                                                        disabled={data?.shiftName !== 'Custom Shift'}
                                                        style={{ borderRadius: '3px' }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '8rem', maxWidth: '8rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'End Time'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <MlxTimePicker
                                                        value={data?.endTime ? dayjs(data?.endTime, 'HH:mm:ss') : null}
                                                        onChange={(time) => {
                                                            form.setFieldValue(`shifts.${rowIndex}.endTime`, time?.format('HH:mm:ss') ?? null);

                                                            // clear selected staffs
                                                            form.setFieldValue(`shifts.${rowIndex}.tenantStaffIds`, []);
                                                        }}
                                                        className={`from-control ${data?.shiftName === 'Custom Shift' && errors?.[rowIndex]?.endTime && 'border-danger'}`}
                                                        popupStyle={{
                                                            zIndex: (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                        }}
                                                        disabled={data?.shiftName !== 'Custom Shift'}
                                                        style={{ borderRadius: '3px' }}
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '8rem', maxWidth: '8rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={''}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <div className="d-flex align-items-center">
                                                        <div className='d-flex align-items-center'>
                                                            <InputSwitch
                                                                checked={data?.isClose}
                                                                onChange={(e: InputSwitchChangeEvent) => {
                                                                    const { value } = e
                                                                    form.setFieldValue(`shifts.${rowIndex}.isClose`, value);
                                                                }}
                                                            />
                                                            <label className='ms-1'>{CLOSE}</label>
                                                        </div>
                                                        <div className='ms-3 d-flex align-items-center'>
                                                            <InputSwitch
                                                                checked={data?.isBd}
                                                                onChange={(e: InputSwitchChangeEvent) => {
                                                                    const { value } = e
                                                                    form.setFieldValue(`shifts.${rowIndex}.isBd`, value);
                                                                }}
                                                            />
                                                            <label className='ms-1' title={BD}>BD</label>
                                                        </div>
                                                    </div>
                                                )
                                            }}
                                            style={{ minWidth: '12rem', maxWidth: '12rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={'Users'}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <MultiSelect
                                                        className={`w-100 ${touched?.[rowIndex]?.tenantStaffIds && errors?.[rowIndex]?.tenantStaffIds && 'border-danger'}`}
                                                        value={data?.tenantStaffIds}
                                                        options={data?.staffs}
                                                        itemTemplate={(option: any) => {
                                                            const checkIfStaffHasShift = getIfStaffHasShift(option, data);
                                                            return (
                                                                <div className='d-flex flex-row align-items-center justify-content-between' key={option.id}>
                                                                    <span>{toPascalCase(option.name)}</span>
                                                                    {!option.isAvailableForSchedule && <i className="ri-calendar-close-line text-danger ms-2"></i>}
                                                                    {checkIfStaffHasShift && <i className="ri-check-line text-success ms-2"></i>}
                                                                </div>
                                                            )
                                                        }}
                                                        onChange={(e) => {
                                                            const { value } = e;
                                                            form.setFieldValue(`shifts.${rowIndex}.tenantStaffIds`, value ?? null);
                                                        }}
                                                        panelClassName={'custom-multiselect'}
                                                        optionLabel='name'
                                                        showSelectAll={true}
                                                        maxSelectedLabels={3}
                                                        optionValue='code'
                                                        filter={true}
                                                        pt={{
                                                            root: { style: { borderRadius: '3px' } },
                                                            header: { style: { padding: '10px' } },
                                                            filterContainer: { className: 'custom-multiselect-filter-container' },
                                                            closeButton: { style: { display: 'none' } },
                                                            panel: {
                                                                style: {
                                                                    "--custom-dropdown-z-index": (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex
                                                                        ? (addMultipleShiftRef?.current as any)?.getElement()?.parentElement?.style?.zIndex + 1
                                                                        : 1056
                                                                }
                                                            }
                                                        }}
                                                        panelStyle={{ maxWidth: '250px' }}
                                                        resetFilterOnHide={true}
                                                        optionDisabled={(option: any) => {
                                                            const checkIfStaffHasShift = getIfStaffHasShift(option, data);

                                                            // check if staff's role is available for schedule or already has the same shift
                                                            if (!option.isAvailableForSchedule || checkIfStaffHasShift) {
                                                                return true;
                                                            }
                                                            return false;
                                                        }}
                                                        showClear={false}
                                                        placeholder={'Select Staffs'}
                                                        panelFooterTemplate={(
                                                            <div className='px-2 py-2 shadow-lg d-flex justify-content-between align-items-center'>
                                                                <div className='d-flex align-items-center'>
                                                                    <JTranslation
                                                                        typeCase="none"
                                                                        text={`${data?.tenantStaffIds?.length} selected`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        disabled={
                                                            !form?.values?.shifts[rowIndex]?.date ||
                                                            !form?.values?.shifts[rowIndex]?.departmentId ||
                                                            !form?.values?.shifts[rowIndex]?.roleId ||
                                                            !form?.values?.shifts[rowIndex]?.shiftName ||
                                                            !form?.values?.shifts[rowIndex]?.startTime ||
                                                            !form?.values?.shifts[rowIndex]?.endTime
                                                        }
                                                    />
                                                )
                                            }}
                                            style={{ minWidth: '12rem', maxWidth: '12rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                        <Column
                                            className="overflow-ellipsis"
                                            header={''}
                                            headerClassName='justify-content-start'
                                            body={(data, { rowIndex }) => {
                                                return (
                                                    <div className="d-flex align-items-center">
                                                        <i
                                                            className="ri-indeterminate-circle-fill delete mx-3"
                                                            role="button"
                                                            title="Remove Shift"
                                                            onClick={() => {
                                                                remove(rowIndex)
                                                            }}
                                                        ></i>
                                                    </div>
                                                )
                                            }}
                                            style={{ width: '5rem' }}
                                            headerStyle={{ height: '50px' }}
                                        />
                                    </DataTable>
                                    <div className="save-btn-section shadow d-flex flex-row justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <Button
                                                className='add-shift-btn'
                                                //type='dashed'
                                                icon={<i className="ri-add-circle-fill"></i>}
                                                onClick={() => {
                                                    push({
                                                        date: '',
                                                        departmentId: '',
                                                        departmentName: '',
                                                        endTime: '',
                                                        isBd: false,
                                                        isClose: false,
                                                        role: '',
                                                        roleId: '',
                                                        shiftName: '',
                                                        startTime: '',
                                                        tenantStaffIds: [] as string[],
                                                        roleOptions: [] as ScheduleRoleDropDown[],
                                                        staffs: cloneDeep(staffList) as PrimeReactDropDown[]
                                                    } as FormData);
                                                }}
                                            >
                                                <small><JTranslation typeCase="pascal" text={ADD_NEW_SHIFT} /></small>
                                            </Button>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <button
                                                className="btn btn-custom-primary-outline"
                                                type="button"
                                                disabled={isLoading}
                                                data-testid="cancel-btn"
                                                onClick={() => {
                                                    if (isLoading) return;

                                                    closeModal();
                                                }}
                                            >
                                                <JTranslation typeCase="pascal" text={CANCEL} />
                                            </button>

                                            <button
                                                className="btn btn-custom-primary"
                                                type="button"
                                                disabled={!form?.isValid || isLoading}
                                                data-testid="save-btn"
                                                onClick={() => {
                                                    if (form?.isValid && !isLoading) {
                                                        form.submitForm();
                                                    }
                                                }}
                                            >
                                                {isLoading ? (
                                                    <div className="d-flex align-items-center justify-content-center">
                                                        <JTranslation typeCase="pascal" text={'Adding'} />
                                                        <i className="pi pi-spin pi-spinner ms-2"></i>
                                                    </div>
                                                ) : (
                                                    <JTranslation typeCase="pascal" text={SAVE} />
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </>
                            )
                        }}
                    </FieldArray>
                </Form>
            </FormikProvider>
        </Sidebar>
    )
}

export default React.memo(AddMultipleShiftToMultipleUsersModal)